import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowRight, CheckCircle, Brain, Heart, Shield, MapPin } from 'lucide-react';
import CTA from '../components/CTA';
import FreeGuideCTA from '../components/FreeGuideCTA';
import PillarClusterNav from '../components/PillarClusterNav';
import { fetchBlogPostsByNiche, BlogPost } from '../utils/blogPosts';

const MedicalAnxietyHypnotherapy: React.FC = () => {
  const [clusterBlogs, setClusterBlogs] = useState<BlogPost[]>([]);
  const [blogsLoading, setBlogsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const posts = await fetchBlogPostsByNiche('medical-anxiety');
      setClusterBlogs(posts);
      setBlogsLoading(false);
    };
    load();
  }, []);

  const medicalServicePages = [
    { href: '/mri-anxiety-hypnotherapy', label: 'MRI Anxiety' },
    { href: '/needle-phobia-hypnotherapy', label: 'Needle Phobia' },
    { href: '/radiotherapy-anxiety', label: 'Radiotherapy Anxiety' },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Can hypnotherapy help with fear of medical procedures?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Hypnotherapy is highly effective for medical procedure anxiety because it works directly with the subconscious fear response. Many clients are able to attend procedures they previously cancelled or avoided.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many sessions do I need before my procedure?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most clients benefit from 2-4 sessions before their procedure. If your appointment is soon, even a single session can make a significant difference to your comfort level.',
        },
      },
      {
        '@type': 'Question',
        name: 'Will I be unconscious during hypnotherapy?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. You remain fully aware and in control throughout. Hypnotherapy is a focused state of relaxation — similar to being absorbed in a book or film. You can open your eyes and stop at any time.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can hypnotherapy help with needle phobia?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely. Needle phobia responds very well to hypnotherapy. By working with the subconscious fear pattern, most clients can have blood tests and injections with significantly reduced anxiety within 2-3 sessions.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do you have experience with hospital-related anxiety?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Sharon volunteers at the Radiotherapy Department at Belfast City Hospital, working directly with patients facing treatment anxiety. This clinical experience informs all of her medical anxiety work.',
        },
      },
    ],
  };

  const faqs = [
    {
      question: 'Can hypnotherapy help with fear of medical procedures?',
      answer: 'Yes. Hypnotherapy is highly effective for medical procedure anxiety because it works directly with the subconscious fear response. Many clients are able to attend procedures they previously cancelled or avoided.',
    },
    {
      question: 'How many sessions do I need before my procedure?',
      answer: 'Most clients benefit from 2-4 sessions before their procedure. If your appointment is soon, even a single session can make a significant difference to your comfort level.',
    },
    {
      question: 'Will I be unconscious during hypnotherapy?',
      answer: 'No. You remain fully aware and in control throughout. Hypnotherapy is a focused state of relaxation — similar to being absorbed in a book or film. You can open your eyes and stop at any time.',
    },
    {
      question: 'Can hypnotherapy help with needle phobia?',
      answer: 'Absolutely. Needle phobia responds very well to hypnotherapy. By working with the subconscious fear pattern, most clients can have blood tests and injections with significantly reduced anxiety within 2-3 sessions.',
    },
    {
      question: 'Do you have experience with hospital-related anxiety?',
      answer: 'Yes. Sharon volunteers at the Radiotherapy Department at Belfast City Hospital, working directly with patients facing treatment anxiety. This clinical experience informs all of her medical anxiety work.',
    },
  ];

  return (
    <main>
      <Helmet>
        <title>Medical Anxiety Hypnotherapy | Sharon Clare | Belfast &amp; NI</title>
        <meta
          name="description"
          content="Fear of medical procedures, MRI anxiety, needle phobia, and radiotherapy anxiety — hypnotherapy can help you face what you need to face. Sharon Clare works with patients across Northern Ireland."
        />
        <meta
          name="keywords"
          content="medical anxiety hypnotherapy, medical procedure anxiety Belfast, hospital anxiety hypnotherapy Northern Ireland"
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/medical-anxiety-hypnotherapy/" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
            Medical Anxiety Hypnotherapy in Northern Ireland
          </h1>
          <p className="text-xl font-heading text-gray-700 mb-6">
            This fear is real, it has a name, and it responds well to hypnotherapy. You can face what you need to face.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/book"
              className="btn-primary inline-flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" /> Request a 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Explanatory section */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            You're Not Alone — Medical Anxiety Is More Common Than You Think
          </h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
            <p>
              Fear of medical procedures affects many more people than those who talk about it. Whether it's a dread of needles, panic at the thought of an MRI, anxiety around a planned operation, or overwhelming fear at the start of radiotherapy — this fear is real, and it can stop people getting the care they need.
            </p>
            <p>
              Medical anxiety takes many forms:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>MRI and scanner anxiety</strong> — claustrophobia and fear of the enclosed space, noise, and loss of control</li>
              <li><strong>Needle phobia</strong> — intense fear of injections, blood tests, or cannulas, sometimes leading to fainting or complete avoidance</li>
              <li><strong>Radiotherapy anxiety</strong> — fear of the mask, the equipment, the isolation of the treatment room, or the broader cancer journey</li>
              <li><strong>Surgery anxiety</strong> — fear of anaesthesia, loss of consciousness, or the procedure itself</li>
              <li><strong>Dental anxiety</strong> — avoidance of dental care due to fear of pain, needles, or being in the chair</li>
            </ul>
            <p>
              One of the most important things to understand about medical anxiety is that avoidance makes it worse. Each time we avoid the feared situation, we send a message to the subconscious mind that the danger is real. The fear grows. Hypnotherapy interrupts this cycle by working directly with the subconscious fear pattern.
            </p>

            <div className="flex items-center gap-2 text-sm text-slate-600 my-4">
              <MapPin className="h-4 w-4 text-teal-600 flex-shrink-0" />
              <span>Volunteers at the Radiotherapy Department, Belfast City Hospital</span>
            </div>

            <p>
              Sharon Clare has direct clinical experience with medical anxiety through her volunteer work at the Radiotherapy Department at Belfast City Hospital, where she supports patients facing cancer treatment. This experience gives her a grounded, compassionate understanding of just how real — and how treatable — procedure-related fear can be.
            </p>
            <p>
              Hypnotherapy for medical anxiety works by retraining the fear response at the subconscious level. In a calm, focused state, the mind becomes receptive to new patterns. Through guided visualisation and suggestion, clients begin to associate the feared procedure with calm and control rather than panic. Most clients are able to attend procedures they had previously cancelled or avoided within 2-4 sessions.
            </p>
            <p>
              Sharon works with clients across Belfast and Northern Ireland, with online sessions also available. Her approach is normalising, compassionate, and built on the understanding that this fear is not a weakness — and that hypnotherapy can help you face what you need to face.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {[
              { icon: <Brain className="w-6 h-6 text-accent-600" />, title: 'Retrain the fear response' },
              { icon: <Shield className="w-6 h-6 text-accent-600" />, title: 'Attend procedures with calm' },
              { icon: <Heart className="w-6 h-6 text-accent-600" />, title: 'Compassionate clinical care' },
              { icon: <MapPin className="w-6 h-6 text-accent-600" />, title: 'BCH clinical experience' },
            ].map(({ icon, title }) => (
              <div key={title} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center space-x-3">
                  {icon}
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PillarClusterNav */}
      <PillarClusterNav
        servicePages={medicalServicePages}
        clusterBlogs={blogsLoading ? [] : clusterBlogs.map(b => ({ slug: b.slug, title: b.title }))}
      />

      {/* Free Guide Lead Magnet */}
      <FreeGuideCTA location="Medical Anxiety Pillar" />

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map(({ question, answer }) => (
              <div key={question} className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-2">{question}</h3>
                    <p className="text-gray-700 leading-relaxed">{answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Ready to Take the Next Step?"
        subtitle="Book a free 10-minute call to discuss how hypnotherapy can help."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default MedicalAnxietyHypnotherapy;
